import React, { useState } from "react";
import { NavLink, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { BrandLogo, COMPANY_NAME } from "@/lib/brand";
import NotificationsBell from "@/components/NotificationsBell";
import {
  LayoutDashboard, ClipboardCheck, ShoppingBag, BarChart3, LogOut,
  Users as UsersIcon, FileText, Wallet, ShieldCheck, CheckSquare,
  ArrowLeftRight, Menu, X, Boxes, UserX, Sparkles, Package, Store, FileSpreadsheet, Inbox,
} from "lucide-react";

/**
 * Unified Promoter Portal shell.
 *
 *  • sales_promoter           → mobile bottom-nav (4 tabs)
 *  • product_manager / admin  → desktop side-nav with full menu
 *  • sales_head / sales_manager → desktop side-nav (read-only modules visible per perm)
 *
 * Each navigation item is gated either by `perm` (permission key) or `roles`.
 * Items the current user can't access are hidden — so the menu is naturally
 * narrower for narrower roles without needing per-role configs.
 *
 * The shell deliberately does NOT render the Service CRM sidebar; users get back
 * to the CRM via the "Back to CRM" link in the header.
 */

const PROMOTER_MOBILE_NAV = [
  { to: "/promoter", label: "Home", icon: LayoutDashboard, end: true },
  { to: "/promoter/attendance", label: "Attendance", icon: ClipboardCheck },
  { to: "/promoter/my-sales", label: "Sales", icon: ShoppingBag },
  { to: "/promoter/stock", label: "Stock", icon: Boxes },
  { to: "/promoter/my-incentive", label: "Earnings", icon: Wallet },
];

const PORTAL_DESKTOP_NAV = [
  { to: "/promoter", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/promoter/approval-center", label: "Approval Center", icon: Inbox },
  { to: "/promoter/manage", label: "Promoter Management", icon: UsersIcon, perm: "promoters.view" },
  { to: "/promoter/attendance-mgmt", label: "Attendance", icon: ClipboardCheck, perm: "promoter_attendance.view" },
  { to: "/promoter/sales-mgmt", label: "Promoter Sales", icon: ShoppingBag, perm: "promoter_sales.view" },
  { to: "/promoter/sales-approval", label: "Sales Approval", icon: CheckSquare, perm: "promoter_sales.approve" },
  { to: "/promoter/stock-mgmt", label: "Stock & Display", icon: Boxes, perm: "promoter_stock.view" },
  { to: "/promoter/financial-rules", label: "Financial Rules", icon: FileText, perm: "promoter_financial.propose" },
  { to: "/promoter/bulk-incentive", label: "Bulk Incentive Setup", icon: FileSpreadsheet, perm: "promoter_financial.propose" },
  { to: "/promoter/financial-approvals", label: "Financial Approvals", icon: ShieldCheck, perm: "promoter_financial.approve" },
  { to: "/promoter/incentive-summary", label: "Incentive Summary", icon: BarChart3, perm: "promoter_sales.view" },
  { to: "/promoter/schemes", label: "Sell-Out Schemes", icon: Sparkles, perm: "promoter_financial.view" },
  { to: "/promoter/terminations", label: "Terminations", icon: UserX, perm: "promoter_termination.view" },
  // Phase 6.1.A — Master data shortcuts in the Promoter Portal sidebar.
  // Visible to anyone with direct create rights (PM gets both; CCR gets Dealers only).
  { to: "/products", label: "Products", icon: Package, perm: "products.add" },
  { to: "/dealers", label: "Dealers", icon: Store, perm: "dealers.add" },
];

function canSeeItem(user, can, item) {
  if (item.roles && !item.roles.includes(user?.role)) return false;
  if (item.perm) return can(item.perm);
  return true;
}

export default function PromoterLayout({ children }) {
  const { user, can } = useAuth();
  const isPromoterRole = user?.role === "sales_promoter";
  if (isPromoterRole) {
    return <MobilePortal user={user}>{children}</MobilePortal>;
  }
  return <DesktopPortal user={user} can={can}>{children}</DesktopPortal>;
}

/* ---------------- Mobile (sales_promoter) ---------------- */
function MobilePortal({ user, children }) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const handleLogout = async () => { await logout(); navigate("/login"); };
  return (
    <div className="min-h-screen bg-slate-50 pb-emergent-safe" data-testid="app-shell-promoter">
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200 flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-2 min-w-0">
          <BrandLogo size={32} testId="brand-logo-promoter-header" />
          <div className="min-w-0">
            <div className="font-heading font-bold text-sm leading-tight truncate">{COMPANY_NAME}</div>
            <div className="text-[10px] text-slate-500 truncate" data-testid="promoter-user-name">{user?.name}</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <NotificationsBell />
          <button onClick={handleLogout}
                  className="px-2 py-1 text-xs text-slate-700 hover:bg-slate-100 rounded"
                  data-testid="logout-button">
            <LogOut size={14} />
          </button>
        </div>
      </header>
      <main className="px-3 py-4">{children}</main>
      <nav className="fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 grid grid-cols-5 z-50 pr-16"
           data-testid="promoter-bottom-nav">
        {PROMOTER_MOBILE_NAV.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink key={item.to} to={item.to} end={item.end || false}
                     className={({ isActive }) =>
                       `flex flex-col items-center gap-0.5 py-2 text-[10px] ${isActive ? "text-slate-900 font-semibold" : "text-slate-500"}`}
                     data-testid={`promoter-nav-${item.label.toLowerCase()}`}>
              <Icon size={18} strokeWidth={2} />
              <span>{item.label}</span>
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}

/* ---------------- Desktop (admin / PM / sales_head / sales_manager) ---------------- */
function DesktopPortal({ user, can, children }) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [open, setOpen] = useState(false);
  const handleLogout = async () => { await logout(); navigate("/login"); };
  const items = PORTAL_DESKTOP_NAV.filter((i) => canSeeItem(user, can, i));

  return (
    <div className="min-h-screen flex bg-slate-50" data-testid="app-shell-promoter-portal">
      {/* sidebar — desktop */}
      <aside className="hidden md:flex md:w-64 border-r border-slate-200 bg-white flex-col">
        <PortalSidebarHeader />
        <PortalSidebarNav items={items} />
        <PortalSidebarFooter user={user} onLogout={handleLogout} />
      </aside>

      {/* mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-40 md:hidden" data-testid="portal-mobile-drawer">
          <div className="fixed inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="fixed left-0 top-0 bottom-0 w-72 bg-white border-r border-slate-200 flex flex-col">
            <div className="flex items-center justify-between px-3 py-3 border-b border-slate-200">
              <span className="font-heading font-bold text-sm">Promoter Portal</span>
              <button onClick={() => setOpen(false)} className="p-1">
                <X size={16} />
              </button>
            </div>
            <PortalSidebarNav items={items} onClick={() => setOpen(false)} />
            <PortalSidebarFooter user={user} onLogout={handleLogout} />
          </aside>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="md:hidden sticky top-0 z-20 bg-white border-b border-slate-200 flex items-center gap-2 px-3 py-2">
          <button onClick={() => setOpen(true)} className="p-2 rounded hover:bg-slate-100"
                  data-testid="portal-menu-btn">
            <Menu size={18} />
          </button>
          <span className="font-heading font-bold text-sm">Promoter Portal</span>
        </header>
        <main className="flex-1 px-4 md:px-6 py-4 md:py-6 overflow-x-hidden">{children}</main>
      </div>
    </div>
  );
}

function PortalSidebarHeader() {
  return (
    <div className="px-4 py-4 border-b border-slate-200 flex items-center gap-2">
      <BrandLogo size={32} testId="brand-logo-portal-header" />
      <div className="min-w-0">
        <div className="font-heading font-bold text-sm leading-tight truncate">Promoter Portal</div>
        <div className="text-[10px] text-slate-500">{COMPANY_NAME}</div>
      </div>
    </div>
  );
}

function PortalSidebarNav({ items, onClick }) {
  return (
    <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-0.5" data-testid="portal-sidebar-nav">
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <NavLink key={item.to} to={item.to} end={item.end || false}
                   onClick={onClick}
                   className={({ isActive }) =>
                     `flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                       isActive
                         ? "bg-slate-900 text-white font-semibold"
                         : "text-slate-700 hover:bg-slate-100"}`}
                   data-testid={`portal-nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <Icon size={16} strokeWidth={2} />
            <span>{item.label}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}

function PortalSidebarFooter({ user, onLogout }) {
  return (
    <div className="border-t border-slate-200 px-3 py-3 space-y-2">
      <Link to="/dashboard" className="flex items-center gap-2 text-xs text-slate-600 hover:text-slate-900"
            data-testid="portal-back-to-crm">
        <ArrowLeftRight size={12} /> Back to Service CRM
      </Link>
      <div className="text-xs text-slate-500" data-testid="portal-user-info">
        <div className="font-medium text-slate-800 truncate">{user?.name}</div>
        <div className="truncate">{(user?.role || "").replace(/_/g, " ")}</div>
      </div>
      <button onClick={onLogout}
              className="w-full text-left flex items-center gap-2 text-xs text-slate-600 hover:text-slate-900"
              data-testid="portal-logout-btn">
        <LogOut size={12} /> Sign out
      </button>
    </div>
  );
}
